import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { useState } from "react";
import { TestTube, CheckCircle, XCircle, AlertCircle, Send, Database } from "lucide-react";

const ContactTest = () => {
  const [testData, setTestData] = useState({
    name: 'Test User',
    email: 'test@example.com',
    message: 'Ceci est un message de test pour vérifier la fonction de contact.'
  });
  const [isTesting, setIsTesting] = useState(false);
  const [testResults, setTestResults] = useState<any>(null);
  const { toast } = useToast();

  const testEdgeFunction = async () => {
    setIsTesting(true);
    setTestResults(null);
    
    try {
      console.log('Testing Edge Function connectivity...');
      
      // Test 1: GET request to check if function is alive
      const { data: getResponse, error: getError } = await supabase.functions.invoke('contact_form_simple_20251219_1830', {
        method: 'GET'
      });
      
      if (getError) {
        throw new Error(`GET test failed: ${getError.message}`);
      }
      
      console.log('GET test successful:', getResponse);
      
      // Test 2: POST request with test data
      const { data: postResponse, error: postError } = await supabase.functions.invoke('contact_form_simple_20251219_1830', {
        body: {
          name: testData.name,
          email: testData.email,
          phone: '079 123 45 67',
          company: 'Test Company',
          subject: 'Test de connectivité',
          message: testData.message
        }
      });
      
      if (postError) {
        throw new Error(`POST test failed: ${postError.message}`);
      }
      
      console.log('POST test successful:', postResponse);
      
      setTestResults({
        getTest: { success: true, data: getResponse },
        postTest: { success: true, data: postResponse },
        overall: 'success'
      });
      
      toast({
        title: "Tests réussis !",
        description: "La fonction Edge fonctionne correctement.",
      });
      
    } catch (error: any) {
      console.error('Test error:', error);
      
      setTestResults({
        getTest: { success: false, error: error.message },
        postTest: { success: false, error: error.message },
        overall: 'failed'
      });
      
      toast({
        title: "Erreur de test",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsTesting(false);
    }
  };

  const testDatabaseConnection = async () => {
    setIsTesting(true);
    
    try {
      console.log('Testing database connection...');
      
      // Test database connection by counting messages
      const { count, error } = await supabase
        .from('contact_messages_20251219_1800')
        .select('*', { count: 'exact', head: true });
      
      if (error) {
        throw new Error(`Database test failed: ${error.message}`);
      }
      
      console.log('Database test successful, message count:', count);
      
      setTestResults(prev => ({
        ...prev,
        dbTest: { success: true, count }
      }));
      
      toast({
        title: "Base de données OK !",
        description: `${count || 0} messages trouvés dans la base.`,
      });
      
    } catch (error: any) {
      console.error('Database test error:', error);
      
      setTestResults(prev => ({
        ...prev,
        dbTest: { success: false, error: error.message }
      }));
      
      toast({
        title: "Erreur base de données",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsTesting(false);
    }
  };

  const sendRealTestMessage = async () => {
    setIsTesting(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('contact_form_simple_20251219_1830', {
        body: {
          name: testData.name,
          email: testData.email,
          phone: '079 123 45 67',
          company: 'Test Company',
          subject: 'Message de test réel',
          message: `${testData.message}\n\nEnvoyé le ${new Date().toLocaleString('fr-FR')}`
        }
      });
      
      if (error) {
        throw error;
      }
      
      toast({
        title: "Message de test envoyé !",
        description: `ID: ${data.id}`,
      });
      
      setTestResults(prev => ({
        ...prev,
        realTest: { success: true, data }
      }));
      
    } catch (error: any) {
      console.error('Real test error:', error);
      
      setTestResults(prev => ({
        ...prev,
        realTest: { success: false, error: error.message }
      }));
      
      toast({
        title: "Erreur d'envoi",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsTesting(false);
    }
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto space-y-8">
          
          {/* Header */}
          <div className="text-center">
            <h1 className="text-3xl font-bold mb-2">Test Formulaire Contact</h1>
            <p className="text-muted-foreground">
              Diagnostiquez les problèmes de connectivité avec les Edge Functions
            </p>
          </div>

          {/* Test Controls */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TestTube className="h-5 w-5" />
                Tests de connectivité
              </CardTitle>
              <CardDescription>
                Vérifiez que tous les composants fonctionnent correctement
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Button 
                  onClick={testEdgeFunction}
                  disabled={isTesting}
                  variant="outline"
                  className="w-full"
                >
                  {isTesting ? 'Test...' : 'Test Edge Function'}
                </Button>
                
                <Button 
                  onClick={testDatabaseConnection}
                  disabled={isTesting}
                  variant="outline"
                  className="w-full"
                >
                  <Database className="h-4 w-4 mr-2" />
                  {isTesting ? 'Test...' : 'Test Base de données'}
                </Button>
                
                <Button 
                  onClick={sendRealTestMessage}
                  disabled={isTesting}
                  className="w-full"
                >
                  <Send className="h-4 w-4 mr-2" />
                  {isTesting ? 'Envoi...' : 'Envoi test réel'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Test Data */}
          <Card>
            <CardHeader>
              <CardTitle>Données de test</CardTitle>
              <CardDescription>
                Modifiez les données utilisées pour les tests
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="testName">Nom</Label>
                <Input
                  id="testName"
                  value={testData.name}
                  onChange={(e) => setTestData(prev => ({ ...prev, name: e.target.value }))}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="testEmail">Email</Label>
                <Input
                  id="testEmail"
                  type="email"
                  value={testData.email}
                  onChange={(e) => setTestData(prev => ({ ...prev, email: e.target.value }))}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="testMessage">Message</Label>
                <Input
                  id="testMessage"
                  value={testData.message}
                  onChange={(e) => setTestData(prev => ({ ...prev, message: e.target.value }))}
                />
              </div>
            </CardContent>
          </Card>

          {/* Test Results */}
          {testResults && (
            <Card>
              <CardHeader>
                <CardTitle>Résultats des tests</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {testResults.getTest && (
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <span>Test GET (fonction active)</span>
                    {testResults.getTest.success ? (
                      <Badge variant="default" className="bg-green-500">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        OK
                      </Badge>
                    ) : (
                      <Badge variant="destructive">
                        <XCircle className="h-3 w-3 mr-1" />
                        Erreur
                      </Badge>
                    )}
                  </div>
                )}
                
                {testResults.postTest && (
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <span>Test POST (envoi données)</span>
                    {testResults.postTest.success ? (
                      <Badge variant="default" className="bg-green-500">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        OK
                      </Badge>
                    ) : (
                      <Badge variant="destructive">
                        <XCircle className="h-3 w-3 mr-1" />
                        Erreur
                      </Badge>
                    )}
                  </div>
                )}
                
                {testResults.dbTest && (
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <span>Base de données</span>
                    {testResults.dbTest.success ? (
                      <Badge variant="default" className="bg-green-500">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        {testResults.dbTest.count} messages
                      </Badge>
                    ) : (
                      <Badge variant="destructive">
                        <XCircle className="h-3 w-3 mr-1" />
                        Erreur
                      </Badge>
                    )}
                  </div>
                )}
                
                {testResults.realTest && (
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <span>Envoi message réel</span>
                    {testResults.realTest.success ? (
                      <Badge variant="default" className="bg-green-500">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Envoyé
                      </Badge>
                    ) : (
                      <Badge variant="destructive">
                        <XCircle className="h-3 w-3 mr-1" />
                        Échec
                      </Badge>
                    )}
                  </div>
                )}
                
                {/* Error Details */}
                {testResults.overall === 'failed' && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-5 w-5 text-red-500 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-red-800">Erreur détectée</h4>
                        <p className="text-sm text-red-700 mt-1">
                          {testResults.getTest?.error || testResults.postTest?.error}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Success Details */}
                {testResults.postTest?.success && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <h4 className="font-medium text-green-800 mb-2">Détails du succès</h4>
                    <pre className="text-xs text-green-700 overflow-auto">
                      {JSON.stringify(testResults.postTest.data, null, 2)}
                    </pre>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Instructions */}
          <Card>
            <CardHeader>
              <CardTitle>Instructions de dépannage</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div>
                  <h4 className="font-medium">Si les tests échouent :</h4>
                  <ul className="text-sm text-muted-foreground mt-1 space-y-1">
                    <li>• Vérifiez la console du navigateur (F12)</li>
                    <li>• Consultez les logs Supabase Dashboard</li>
                    <li>• Vérifiez la connexion internet</li>
                    <li>• Attendez quelques secondes et réessayez</li>
                  </ul>
                </div>
                
                <div>
                  <h4 className="font-medium">Fonctions Edge disponibles :</h4>
                  <ul className="text-sm text-muted-foreground mt-1 space-y-1">
                    <li>• contact_form_simple_20251219_1830 (version diagnostic)</li>
                    <li>• contact_form_20251219_1800 (version complète)</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default ContactTest;